// SetupWizard — operator-driven guided customer onboarding.
//
// This is the richer, guided flow on top of the bare PlatformConsole create form.
// It DRIVES the proven Stage B engine: it provisions a tenant via the unchanged
// POST /api/platform/tenants (provision_tenant — seeds base defaults + a starter
// MSA extraction profile + a starter CDA playbook), then records the operator's
// onboarding answers via PUT /api/platform/tenants/:id/onboarding (persisted in
// tenants.config; no new provisioning core, no schema change).
//
// Operator-only: the server-side gate (routes/platformAdmin.js:operatorOnly,
// audited) is authoritative; this component is only mounted for is_operator.
//
// HONESTY RULE (first-class): capabilities that exist today are fully implemented;
// capabilities that need building are RECORDED but shown disabled/"coming soon",
// never as dead working controls. Secrets are never collected into config — the
// own-mailbox / Harvey / SharePoint credential entry lives in the existing
// encrypted-at-rest settings panels (ssoCrypto / configStore), shown once there.
//
//   LIVE today:      SSO-vs-password (force 2FA only when SSO absent), own-mailbox
//                    vs KontractAI-default, show-only-what-is-provisioned for
//                    Harvey/Salesforce/e-sign/SharePoint/IP-allowlist.
//   DEFERRED (recorded, disabled): per-tenant AI key capture, e-sign-absent
//                    contract workflow, auto-provisioned SharePoint, directory
//                    import. AI routing config is locked until a customer key
//                    exists — the gate is wired now so it unlocks the moment
//                    per-tenant key capture lands.

import { useState, useEffect, useCallback } from "react";
import { T } from "../theme";
import { apiCall, apiCallEx } from "../apiClient";
import { Btn, Inp, Sel, Modal, Card, Badge, SectionTitle } from "./primitives";

const PLATFORM_TENANT_ID = "00000000-0000-0000-0000-000000000001";

type Signin = "password" | "sso";
type EmailMode = "own" | "kontractai_default";
type SharePointMode = "own" | "none";

interface PlannedUser { email: string; role: string; }

interface WizardForm {
  // 1. Organization
  name: string;
  edition: string;
  // 2. Sign-in & first admin
  signin: Signin;
  admin_name: string;
  admin_email: string;
  sso_domains: string; // comma/space separated
  // 3. Entities & users
  entities: string; // one per line
  planned_users: PlannedUser[];
  // 4. Email
  email_mode: EmailMode;
  // 5. Integrations
  harvey: boolean;
  salesforce: boolean;
  esign_used: boolean;
  sharepoint_mode: SharePointMode;
  ip_allowlist: string; // one per line
  // 6. AI
  ai_byo_intent: boolean;
}

const EMPTY: WizardForm = {
  name: "", edition: "firm",
  signin: "password", admin_name: "", admin_email: "", sso_domains: "",
  entities: "", planned_users: [],
  email_mode: "kontractai_default",
  harvey: false, salesforce: false, esign_used: true, sharepoint_mode: "none", ip_allowlist: "",
  ai_byo_intent: false,
};

const STEPS = ["Organization", "Sign-in & admin", "Entities & users", "Email", "Integrations", "AI", "Review & create"];

// ── Draft-save / resume ──────────────────────────────────────────────────────
// The in-progress wizard form is persisted to localStorage (keyed per operator)
// so a stop, reload, or one of the hard-reset vectors (the apiClient 401-reload,
// the App-level render-error boundary) never loses entered work. The draft is
// cleared on a successful create/reactivate and via an explicit "discard".
function wizardDraftKey(): string {
  let uid = "anon";
  try { uid = (JSON.parse(localStorage.getItem("kontractai_user") || "{}") || {}).id || "anon"; } catch { /* ignore */ }
  return `kontractai_wizard_draft_${uid}`;
}
function loadWizardDraft(): { form?: any; step?: number } | null {
  try { const raw = localStorage.getItem(wizardDraftKey()); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function clearWizardDraft() {
  try { localStorage.removeItem(wizardDraftKey()); } catch { /* ignore */ }
}

interface ReactivateOffer { tenant_id: string; tenant_name?: string; status?: string; message?: string }

const ROLE_OPTIONS = [
  { value: "requester", label: "Requester" },
  { value: "lawyer", label: "Lawyer" },
  { value: "legalops", label: "Legal Ops" },
  { value: "commercial_ops", label: "Commercial Ops" },
  { value: "contract_manager", label: "Contract Manager" },
  { value: "executive", label: "Executive" },
  { value: "admin", label: "Admin" },
];

function splitLines(s: string): string[] {
  return s.split(/[\n,]+/).map(x => x.trim()).filter(Boolean);
}

// A muted "deferred / coming soon" notice — used uniformly so deferred capabilities
// read as honestly-not-built, never as broken controls.
const ComingSoon = ({ children }: { children: any }) => (
  <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: 6, backgroundColor: T.bg3, border: `1px dashed ${T.brd}`, color: T.txD, fontSize: 12, display: "flex", gap: 8, alignItems: "flex-start" }}>
    <span style={{ fontWeight: 700 }}>⏳ Coming soon</span>
    <span>{children}</span>
  </div>
);

const StepNote = ({ children, tone = "info" }: { children: any; tone?: "info" | "warn" }) => (
  <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 6, fontSize: 12.5,
    backgroundColor: tone === "warn" ? T.ylwS : T.bluS,
    color: tone === "warn" ? T.ylw : T.txM,
    border: `1px solid ${tone === "warn" ? T.ylw : T.brd}` }}>
    {children}
  </div>
);

const Check = ({ checked, onChange, label, sub }: { checked: boolean; onChange: (v: boolean) => void; label: string; sub?: string }) => (
  <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "10px 12px", borderRadius: 8, border: `1px solid ${checked ? T.acc : T.brd}`, backgroundColor: checked ? T.accS : "transparent", marginBottom: 8 }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
    <div>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.tx }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: T.txM, marginTop: 2 }}>{sub}</div>}
    </div>
  </label>
);

export const SetupWizard = () => {
  const [step, setStep] = useState<number>(() => { const d = loadWizardDraft(); return typeof d?.step === "number" ? d.step : 0; });
  const [form, setForm] = useState<WizardForm>(() => { const d = loadWizardDraft(); return d?.form ? { ...EMPTY, ...d.form } : { ...EMPTY }; });
  const [restoredDraft, setRestoredDraft] = useState<boolean>(() => !!loadWizardDraft());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Wizard completion (create or reactivate): the admin's set-password invite was
  // sent (or failed) — NO password is shown. reactivated distinguishes the path.
  // provisioning = per-record results for entered entities + users; carried_over =
  // pre-existing users/entities (surfaced on reactivate so the operator can reconcile).
  const [completion, setCompletion] = useState<{
    name: string; admin_email: string; sent: boolean; error?: string; reactivated: boolean;
    provisioning?: { entities: any[]; users: any[] };
    carried_over?: { users: any[]; entities: any[] };
  } | null>(null);
  // Offboarded-match reactivate offer.
  const [reactivateOffer, setReactivateOffer] = useState<ReactivateOffer | null>(null);
  const [reactivating, setReactivating] = useState(false);

  const set = useCallback(<K extends keyof WizardForm>(k: K, v: WizardForm[K]) => {
    setForm(f => ({ ...f, [k]: v }));
  }, []);

  useEffect(() => { setErr(""); }, [step]);

  // Persist the draft on every change; clear it once the form returns to pristine
  // (which reset() does), so a successful create/reactivate leaves no stale draft.
  useEffect(() => {
    const pristine = step === 0 && JSON.stringify(form) === JSON.stringify(EMPTY);
    try {
      if (pristine) clearWizardDraft();
      else localStorage.setItem(wizardDraftKey(), JSON.stringify({ form, step }));
    } catch { /* storage unavailable — skip */ }
  }, [form, step]);

  const reset = () => {
    clearWizardDraft();
    setForm({ ...EMPTY }); setStep(0); setErr(""); setCompletion(null);
    setReactivateOffer(null); setRestoredDraft(false);
  };

  // The onboarding answers recorded on the tenant config (shared by create + reactivate).
  const buildOnboarding = () => ({
    signin_method: form.signin,
    sso: form.signin === "sso" ? { allowed_domains: splitLines(form.sso_domains) } : undefined,
    entities: splitLines(form.entities),
    planned_users: form.planned_users.filter(u => u.email.trim()).map(u => ({ email: u.email.trim().toLowerCase(), role: u.role })),
    email_mode: form.email_mode,
    integrations: {
      harvey: form.harvey, salesforce: form.salesforce, esign_used: form.esign_used,
      sharepoint_mode: form.sharepoint_mode, ip_allowlist: splitLines(form.ip_allowlist),
    },
    ai: { byo_intent: form.ai_byo_intent },
  });

  // ── Per-step validation gate ───────────────────────────────────────────────
  const stepValid = (): string => {
    if (step === 0 && !form.name.trim()) return "Organization name is required.";
    if (step === 1) {
      if (!form.admin_email.trim()) return "A first admin email is required (the break-glass admin, even with SSO).";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.admin_email.trim())) return "Enter a valid first admin email.";
    }
    return "";
  };

  const next = () => {
    const v = stepValid();
    if (v) { setErr(v); return; }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep(s => Math.max(s - 1, 0));

  const addUser = () => set("planned_users", [...form.planned_users, { email: "", role: "requester" }]);
  const updUser = (i: number, patch: Partial<PlannedUser>) =>
    set("planned_users", form.planned_users.map((u, j) => j === i ? { ...u, ...patch } : u));
  const rmUser = (i: number) => set("planned_users", form.planned_users.filter((_, j) => j !== i));

  // ── Provision + record ──────────────────────────────────────────────────────
  const submit = async () => {
    const v = stepValid();
    if (v) { setErr(v); return; }
    setBusy(true); setErr("");

    // 1. Provision via the proven engine. apiCallEx exposes the status so every
    // failure is shown IN PLACE on this Review step — we never navigate away and
    // never lose the operator's entered input.
    const provRes = await apiCallEx("/platform/tenants", {
      method: "POST",
      body: JSON.stringify({
        name: form.name.trim(),
        edition: form.edition,
        admin_name: form.admin_name.trim() || undefined,
        admin_email: form.admin_email.trim().toLowerCase(),
      }),
    });
    if (provRes.status === 409) {
      const d = provRes.data || {};
      setBusy(false);
      // Matches a soft-offboarded/suspended customer → offer to reactivate it
      // instead of blocking forever (the backend created nothing).
      if (d.error === "offboarded_match" && d.tenant_id) {
        setReactivateOffer({ tenant_id: d.tenant_id, tenant_name: d.tenant_name, status: d.status, message: d.message });
        return;
      }
      // admin_email_in_use / name_in_use / ambiguous_match / any other 409 — show
      // the specific message in place; stay on Review with input intact.
      setErr(d.message || "That name or admin email is already in use — choose another. No customer was created.");
      return;
    }
    const prov = provRes.data;
    if (!provRes.ok || !prov) { setBusy(false); setErr("Provisioning failed. Please check the details and try again — your entries are kept."); return; }
    if (!prov.created) {
      setBusy(false);
      setErr(`A customer named "${form.name.trim()}" already exists — no changes were made. Choose a different name.`);
      return;
    }

    // 2. Record the onboarding answers on the tenant config (no secrets).
    const rec = await apiCall(`/platform/tenants/${prov.tenant_id}/onboarding`, {
      method: "PUT",
      body: JSON.stringify(buildOnboarding()),
    });
    setBusy(false);
    // The admin sets their own password via the auto-sent invite — no credential shown.
    setCompletion({
      name: form.name.trim(), admin_email: prov.admin_email, sent: !!prov.invite_sent, error: prov.invite_error, reactivated: false,
      provisioning: rec?.provisioning, carried_over: rec?.carried_over,
    });
    if (!rec) setErr("Customer created, but recording the onboarding answers failed — you can re-run the wizard's later steps from the Platform console.");
  };

  // Reactivate an offboarded/suspended match AND provision the entered admin +
  // auto-send the set-password invite (reactivate-onboard). Restores the customer
  // to active with a usable admin. NEVER silently re-keys a pre-existing account:
  // a 409 (email belongs to a different customer) is surfaced in place.
  const doReactivate = async () => {
    if (!reactivateOffer) return;
    setReactivating(true); setErr("");
    const res = await apiCallEx(`/platform/tenants/${reactivateOffer.tenant_id}/reactivate-onboard`, {
      method: "POST",
      body: JSON.stringify({ admin_email: form.admin_email.trim().toLowerCase(), admin_name: form.admin_name.trim() || undefined }),
    });
    if (res.status === 409) {
      setReactivating(false); setReactivateOffer(null);
      setErr(res.data?.message || "That admin email belongs to a different customer — choose a different admin email.");
      return;
    }
    const d = res.data;
    if (!res.ok || !d) { setReactivating(false); setReactivateOffer(null); setErr("Reactivation failed — retry, or use the Platform console."); return; }
    // Record onboarding for the reactivated customer + provision entered records.
    const rec = await apiCall(`/platform/tenants/${reactivateOffer.tenant_id}/onboarding`, { method: "PUT", body: JSON.stringify(buildOnboarding()) });
    setReactivating(false);
    const name = reactivateOffer.tenant_name || form.name.trim();
    setReactivateOffer(null);
    setCompletion({
      name, admin_email: d.admin_email, sent: !!d.invite_sent, error: d.invite_error, reactivated: true,
      provisioning: rec?.provisioning, carried_over: rec?.carried_over,
    });
    // Done; clear the draft + form (pristine → effect clears storage).
    clearWizardDraft();
    setForm({ ...EMPTY }); setStep(0); setRestoredDraft(false);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const provisionedIntegrations = [
    form.harvey && "Harvey",
    form.salesforce && "Salesforce",
    form.esign_used && "E-signature",
    form.sharepoint_mode === "own" && "SharePoint",
    splitLines(form.ip_allowlist).length > 0 && "IP allowlist",
  ].filter(Boolean) as string[];

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: "0 auto" }}>
      <SectionTitle sub="Operator-only · guided customer onboarding on the shared Stage B engine">
        Setup Wizard
      </SectionTitle>

      {/* Stepper */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "8px 0 22px" }}>
        {STEPS.map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              backgroundColor: i === step ? T.acc : i < step ? T.grnS : T.bg3,
              color: i === step ? "#fff" : i < step ? T.grn : T.txD,
              border: `1px solid ${i === step ? T.acc : i < step ? T.grn : T.brd}`,
            }}>
              <span>{i < step ? "✓" : i + 1}</span>{s}
            </div>
          </div>
        ))}
      </div>

      {/* Draft auto-save indicator + discard. Shown whenever there is in-progress work. */}
      {!(step === 0 && JSON.stringify(form) === JSON.stringify(EMPTY)) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, padding: "7px 12px", borderRadius: 6, backgroundColor: T.bg3, border: `1px solid ${T.brd}`, fontSize: 12, color: T.txM }}>
          <span>{restoredDraft ? "Draft restored — " : ""}Your progress is saved automatically and survives a reload.</span>
          <Btn v="ghost" sz="sm" onClick={reset}>Discard draft &amp; start over</Btn>
        </div>
      )}

      {err && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 6, backgroundColor: T.redS, color: T.red, fontSize: 13, border: `1px solid ${T.red}` }}>
          {err}
        </div>
      )}

      <Card>
        {/* ── 0. Organization ── */}
        {step === 0 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Organization</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              The legal/display name and edition. The engine seeds base defaults, a starter MSA
              extraction profile, and a starter CDA playbook — everything else is editable later.
            </p>
            <Inp label="Organization name" value={form.name} onChange={v => set("name", v)} placeholder="Acme Corporation" required />
            <Sel label="Edition" value={form.edition} onChange={v => set("edition", v)}
              options={[{ value: "firm", label: "Firm (outside counsel)" }, { value: "in_house", label: "In-House (corporate legal)" }]} />
          </div>
        )}

        {/* ── 1. Sign-in & first admin ── */}
        {step === 1 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Sign-in & first admin</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              How the customer's users sign in. A first admin is always created as a working break-glass
              login.
            </p>
            <div style={{ display: "flex", gap: 18, marginBottom: 14 }}>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 14, color: T.tx }}>
                <input type="radio" checked={form.signin === "password"} onChange={() => set("signin", "password")} /> Password (no SSO)
              </label>
              <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 14, color: T.tx }}>
                <input type="radio" checked={form.signin === "sso"} onChange={() => set("signin", "sso")} /> Microsoft SSO
              </label>
            </div>

            <Inp label="First admin name" value={form.admin_name} onChange={v => set("admin_name", v)} placeholder="Jane Admin" />
            <Inp label="First admin email" value={form.admin_email} onChange={v => set("admin_email", v)} placeholder="admin@acme.com" type="email" required />

            {form.signin === "password" ? (
              <StepNote tone="warn">
                <strong>Two-factor will be forced</strong> at first login for this tenant (no SSO present),
                via the existing TOTP setup gate. Set <code>config.force_2fa</code> is recorded and enforced
                at sign-in.
              </StepNote>
            ) : (
              <>
                <Inp label="Allowed email domains" value={form.sso_domains} onChange={v => set("sso_domains", v)} placeholder="acme.com, acme.co.uk" />
                <StepNote>
                  Users arrive via <strong>JIT provisioning</strong> on first SSO sign-in. No forced 2FA —
                  the customer's identity provider supplies the second factor. The directory + client secret
                  are configured (encrypted) by the tenant admin in <strong>Administration → SSO Providers</strong> after creation.
                </StepNote>
              </>
            )}
          </div>
        )}

        {/* ── 2. Entities & users ── */}
        {step === 2 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Entities & initial users</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              The customer's client entities, and an optional first set of users with roles. Minimal-first:
              everything here is recorded and editable later.
            </p>
            <Inp label="Client entities (one per line)" value={form.entities} onChange={v => set("entities", v)} textarea placeholder={"Acme Holdings\nAcme EU GmbH"} />

            <div style={{ marginTop: 14 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: T.txD, marginBottom: 6, letterSpacing: ".04em", textTransform: "uppercase" }}>Initial users</label>
              {form.signin === "sso" && (
                <StepNote>Optional with SSO — most users arrive automatically via JIT on first sign-in. Listing them here just records the intended roster.</StepNote>
              )}
              {form.planned_users.map((u, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-start" }}>
                  <div style={{ flex: 2 }}><Inp value={u.email} onChange={v => updUser(i, { email: v })} placeholder="user@acme.com" type="email" /></div>
                  <div style={{ flex: 1 }}><Sel value={u.role} onChange={v => updUser(i, { role: v })} options={ROLE_OPTIONS} /></div>
                  <Btn v="ghost" sz="sm" onClick={() => rmUser(i)}>✕</Btn>
                </div>
              ))}
              <div style={{ marginTop: 8 }}><Btn v="secondary" sz="sm" onClick={addUser}>+ Add user</Btn></div>
            </div>
          </div>
        )}

        {/* ── 3. Email ── */}
        {step === 3 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Email</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              How outbound/inbound mail is sent for this tenant.
            </p>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "12px 14px", borderRadius: 8, border: `1px solid ${form.email_mode === "kontractai_default" ? T.acc : T.brd}`, backgroundColor: form.email_mode === "kontractai_default" ? T.accS : "transparent", marginBottom: 8 }}>
              <input type="radio" checked={form.email_mode === "kontractai_default"} onChange={() => set("email_mode", "kontractai_default")} style={{ marginTop: 3 }} />
              <div><div style={{ fontSize: 14, fontWeight: 600, color: T.tx }}>KontractAI default mailbox</div><div style={{ fontSize: 12, color: T.txM }}>Sensible default — sends via the shared KontractAI Microsoft Graph app (replies@/noreply@kontractai.com). Nothing to configure.</div></div>
            </label>
            <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", padding: "12px 14px", borderRadius: 8, border: `1px solid ${form.email_mode === "own" ? T.acc : T.brd}`, backgroundColor: form.email_mode === "own" ? T.accS : "transparent" }}>
              <input type="radio" checked={form.email_mode === "own"} onChange={() => set("email_mode", "own")} style={{ marginTop: 3 }} />
              <div><div style={{ fontSize: 14, fontWeight: 600, color: T.tx }}>Customer's own mailbox</div><div style={{ fontSize: 12, color: T.txM }}>The customer brings their own Microsoft 365 tenant + Graph app.</div></div>
            </label>
            {form.email_mode === "own" && (
              <StepNote>
                The Graph tenant/client ID/secret are entered (write-only, <strong>encrypted at rest</strong>) by
                the tenant admin in <strong>Administration → Email settings</strong> — secrets are never collected
                into the wizard or stored in tenant config.
              </StepNote>
            )}
          </div>
        )}

        {/* ── 4. Integrations (show only what is provisioned) ── */}
        {step === 4 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Integrations</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              <strong>Show only what is provisioned:</strong> a capability the customer hasn't enabled here is
              hidden for that tenant — never a dead control. Secrets are entered later (encrypted) in
              Administration; this step records which capabilities the tenant gets.
            </p>

            <Check checked={form.harvey} onChange={v => set("harvey", v)} label="Harvey AI" sub="Enable only if the customer has a Harvey key. Off → Harvey is hidden entirely for this tenant." />
            <Check checked={form.salesforce} onChange={v => set("salesforce", v)} label="Salesforce" sub="Enable only if the customer uses Salesforce. Off → Salesforce is hidden for this tenant." />

            <div style={{ marginTop: 4 }}>
              <Check checked={form.esign_used} onChange={v => set("esign_used", v)} label="E-signature (DocuSign)" sub="Does this customer use e-signature? Off → e-sign is hidden for this tenant." />
              {!form.esign_used && (
                <ComingSoon>
                  With e-sign off, finalized agreements need a "finalized / pending signature" state with a
                  manual path to upload the executed copy, mark it executed, and enqueue AI extraction. That
                  workflow change is a <strong>dependent build</strong> — your answer is recorded now; it is not built yet.
                </ComingSoon>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.tx, marginBottom: 6 }}>SharePoint</label>
              <div style={{ display: "flex", gap: 18, marginBottom: 4 }}>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 14, color: T.tx }}>
                  <input type="radio" checked={form.sharepoint_mode === "none"} onChange={() => set("sharepoint_mode", "none")} /> Not used
                </label>
                <label style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 14, color: T.tx }}>
                  <input type="radio" checked={form.sharepoint_mode === "own"} onChange={() => set("sharepoint_mode", "own")} /> Customer brings their own
                </label>
              </div>
              {form.sharepoint_mode === "own"
                ? <StepNote>The customer's SharePoint host + credentials are configured (encrypted) by the tenant admin in <strong>Administration → SharePoint</strong>.</StepNote>
                : <ComingSoon>Auto-provisioning a KontractAI-hosted SharePoint site for customers without their own is a later project. Recorded as "not used" for now.</ComingSoon>}
            </div>

            <div style={{ marginTop: 12 }}>
              <Inp label="IP allowlist (one CIDR/IP per line — optional)" value={form.ip_allowlist} onChange={v => set("ip_allowlist", v)} textarea placeholder={"203.0.113.0/24\n198.51.100.7"} />
            </div>
          </div>
        )}

        {/* ── 5. AI ── */}
        {step === 5 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>AI</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              Every customer runs on the <strong>KontractAI default AI keys</strong> today.
            </p>

            <Check checked={form.ai_byo_intent} onChange={v => set("ai_byo_intent", v)} label="Customer intends to bring their own AI key" sub="Records intent only — per-tenant key capture is not built yet (see below)." />

            <ComingSoon>
              <strong>Per-tenant AI key capture</strong> isn't built yet, so the wizard does not collect a customer key.
              The intent above is recorded; when key capture lands, the routing lock below unlocks automatically.
            </ComingSoon>

            {/* AI routing config — LOCKED unless a customer key is present (never today) */}
            <div style={{ marginTop: 14, padding: "14px 16px", borderRadius: 8, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, opacity: 0.85 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15 }}>🔒</span>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.txM }}>AI routing configuration — locked</div>
              </div>
              <div style={{ fontSize: 12.5, color: T.txD, marginTop: 6 }}>
                On the shared default keys, model routing is managed by KontractAI and is not the customer's to
                change. This panel unlocks only for a customer that has brought their own AI key — the gate is
                wired now so it behaves correctly the moment per-tenant key capture lands.
              </div>
            </div>
          </div>
        )}

        {/* ── 6. Review & create ── */}
        {step === 6 && (
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 17, color: T.tx }}>Review & create</h3>
            <p style={{ fontSize: 13, color: T.txM, margin: "0 0 16px" }}>
              Confirm the summary, then provision. The first admin's temporary password is shown once.
            </p>
            <div style={{ border: `1px solid ${T.brd}`, borderRadius: 8, overflow: "hidden" }}>
              {[
                ["Organization", form.name || "—"],
                ["Edition", form.edition === "in_house" ? "In-House" : "Firm"],
                ["Sign-in", form.signin === "sso" ? `Microsoft SSO${splitLines(form.sso_domains).length ? ` · ${splitLines(form.sso_domains).join(", ")}` : ""}` : "Password"],
                ["First admin", `${form.admin_name || "Tenant Admin"} · ${form.admin_email || "—"}`],
                ["Force 2FA", form.signin === "password" ? "Yes (no SSO)" : "No (SSO supplies 2FA)"],
                ["Client entities", splitLines(form.entities).join(", ") || "—"],
                ["Initial users", form.planned_users.filter(u => u.email.trim()).length ? form.planned_users.filter(u => u.email.trim()).map(u => `${u.email} (${u.role})`).join(", ") : (form.signin === "sso" ? "via JIT" : "—")],
                ["Email", form.email_mode === "own" ? "Customer's own mailbox" : "KontractAI default"],
                ["Provisioned integrations", provisionedIntegrations.length ? provisionedIntegrations.join(", ") : "None"],
                ["AI", `KontractAI default keys${form.ai_byo_intent ? " · BYO key intent recorded (capture coming soon)" : ""}`],
              ].map(([k, v], i) => (
                <div key={k as string} style={{ display: "flex", padding: "9px 14px", fontSize: 13, backgroundColor: i % 2 ? T.bg2 : "transparent", borderBottom: `1px solid ${T.brdSubtle}` }}>
                  <div style={{ width: 200, color: T.txM, fontWeight: 600 }}>{k}</div>
                  <div style={{ flex: 1, color: T.tx }}>{v}</div>
                </div>
              ))}
            </div>
            <StepNote>
              Deferred items recorded but not built: per-tenant AI key capture, e-sign-absent contract
              workflow{!form.esign_used ? " (applies — e-sign is off)" : ""}, auto-provisioned SharePoint, directory
              import. These are persisted on the tenant config for the dependent builds to consume.
            </StepNote>
          </div>
        )}

        {/* Footer nav */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24, paddingTop: 16, borderTop: `1px solid ${T.brdSubtle}` }}>
          <Btn v="ghost" onClick={back} disabled={step === 0 || busy}>← Back</Btn>
          {step < STEPS.length - 1
            ? <Btn v="primary" onClick={next}>Next →</Btn>
            : <Btn v="primary" onClick={submit} disabled={busy}>{busy ? "Provisioning…" : "Create tenant"}</Btn>}
        </div>
      </Card>

      {/* ─── Completion: invite sent (create OR reactivate). NO password is shown. ─── */}
      <Modal open={!!completion} onClose={() => { setCompletion(null); reset(); }} title={completion?.reactivated ? "Customer Reactivated" : "Customer Created"} w={520}>
        {completion && (
          <div>
            <p style={{ fontSize: 14, color: T.tx, margin: "0 0 14px" }}>
              <strong>{completion.name}</strong> {completion.reactivated ? "is active again, and its admin account has been provisioned." : "has been created and its admin account staged."}
            </p>
            {completion.sent ? (
              <div style={{ padding: "12px 14px", borderRadius: 6, backgroundColor: T.grnS, color: T.grn, fontSize: 13, border: `1px solid ${T.grn}`, marginBottom: 16 }}>
                ✓ An invitation to set their password has been sent to <strong>{completion.admin_email}</strong> from KontractAI.
                The link is single-use and expires. No password is shown here — the admin sets their own.
              </div>
            ) : (
              <div style={{ padding: "12px 14px", borderRadius: 6, backgroundColor: T.ylwS, color: T.ylw, fontSize: 13, border: `1px solid ${T.ylw}`, marginBottom: 16, fontWeight: 600 }}>
                ⚠ {completion.name} was {completion.reactivated ? "reactivated" : "created"}, but the invitation email to{" "}
                <strong>{completion.admin_email}</strong> could not be sent. Use <strong>Resend invite</strong> in the
                Platform console to try again.
              </div>
            )}

            {/* Per-record provisioning summary — truthful: created vs skipped/failed. */}
            {completion.provisioning && (() => {
              const ents = completion.provisioning.entities || [];
              const usrs = completion.provisioning.users || [];
              const entCreated = ents.filter((e: any) => e.status === "created");
              const entOther = ents.filter((e: any) => e.status !== "created");
              const usrCreated = usrs.filter((u: any) => u.status === "created");
              const usrOther = usrs.filter((u: any) => u.status !== "created");
              const reasonText: Record<string, string> = { email_in_use: "email already in use", name_in_use: "name already in use", create_failed: "could not be created", insert_failed: "could not be created", lookup_failed: "could not be checked", exists: "already exists" };
              if (ents.length === 0 && usrs.length === 0) return null;
              return (
                <div style={{ border: `1px solid ${T.brd}`, borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12.5 }}>
                  <div style={{ fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontSize: 11, marginBottom: 6 }}>Entities & users created</div>
                  {ents.length > 0 && (
                    <div style={{ marginBottom: usrs.length ? 8 : 0 }}>
                      <span style={{ color: T.txM }}>Company entities: </span>
                      {entCreated.length > 0 && <span style={{ color: T.grn }}>{entCreated.length} created ({entCreated.map((e: any) => e.name).join(", ")}). </span>}
                      {entOther.map((e: any, i: number) => <span key={i} style={{ color: e.status === "exists" ? T.txM : T.ylw }}>{e.name}: {reasonText[e.reason || e.status] || e.status}. </span>)}
                      {entCreated.length === 0 && entOther.length === 0 && <span style={{ color: T.txM }}>none entered.</span>}
                    </div>
                  )}
                  {usrs.length > 0 && (
                    <div>
                      <span style={{ color: T.txM }}>Additional users: </span>
                      {usrCreated.map((u: any, i: number) => <span key={i} style={{ color: u.invited ? T.grn : T.ylw }}>{u.email} {u.invited ? "created + invited" : "created, invite failed (Resend)"}. </span>)}
                      {usrOther.map((u: any, i: number) => <span key={i} style={{ color: u.status === "exists" ? T.txM : T.ylw }}>{u.email}: {reasonText[u.reason || u.status] || u.status}. </span>)}
                      {usrCreated.length === 0 && usrOther.length === 0 && <span style={{ color: T.txM }}>none entered.</span>}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Reactivation: surface carried-over users/entities so the operator can reconcile near-duplicates. */}
            {completion.reactivated && completion.carried_over && (completion.carried_over.users?.length > 0 || completion.carried_over.entities?.length > 0) && (
              <div style={{ border: `1px dashed ${T.brd}`, borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12.5, backgroundColor: T.bg3 }}>
                <div style={{ fontWeight: 700, color: T.txD, textTransform: "uppercase", letterSpacing: ".04em", fontSize: 11, marginBottom: 6 }}>Already on this customer — review for duplicates</div>
                {completion.carried_over.users?.length > 0 && <div style={{ color: T.txM }}>Users: {completion.carried_over.users.map((u: any) => u.email).join(", ")}</div>}
                {completion.carried_over.entities?.length > 0 && <div style={{ color: T.txM }}>Entities: {completion.carried_over.entities.map((e: any) => e.name).join(", ")}</div>}
                <div style={{ color: T.txD, marginTop: 4 }}>These were carried over from before offboard. Remove any duplicates from the customer's admin area.</div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn v="primary" onClick={() => { setCompletion(null); reset(); }}>Done</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Offboarded/suspended match → offer to reactivate (not a duplicate, not a block) ─── */}
      <Modal open={!!reactivateOffer} onClose={() => setReactivateOffer(null)} title="Existing customer found" w={520}>
        {reactivateOffer && (
          <div>
            <p style={{ fontSize: 14, color: T.tx, margin: "0 0 12px" }}>
              {reactivateOffer.message || `This matches a previously ${reactivateOffer.status || "offboarded"} customer${reactivateOffer.tenant_name ? ` “${reactivateOffer.tenant_name}”` : ""}.`}
            </p>
            <div style={{ padding: "10px 12px", borderRadius: 6, backgroundColor: T.bluS, color: T.txM, fontSize: 12.5, border: `1px solid ${T.brd}`, marginBottom: 18 }}>
              Reactivating restores the existing customer to <strong>active</strong>, provisions the entered admin, and
              emails them a <strong>single-use set-password link</strong> from KontractAI. It does <strong>not</strong> create a
              duplicate and <strong>never</strong> overwrites another customer's account.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Btn v="ghost" onClick={() => setReactivateOffer(null)} disabled={reactivating}>Cancel</Btn>
              <Btn v="primary" onClick={doReactivate} disabled={reactivating}>{reactivating ? "Reactivating…" : `Reactivate ${reactivateOffer.tenant_name || "customer"}`}</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
